import {Component, OnDestroy, OnInit} from '@angular/core';
import {AlertComponent} from '../../../shared/components/alert/alert.component';
import {LoadingComponent} from '../../../shared/components/loading/loading.component';
import {PaginationComponent} from '../../../shared/components/pagination/pagination.component';
import {TranslateModule} from '@ngx-translate/core';
import {
  createPaginationState,
  setPaginationLoading,
  updatePaginationFromResult,
} from '../../../core/helpers/pagination-state.helper';
import {ShopSellerListFilterModel, ShopSellerListItem} from '../../../core/models/shop-seller/shop-seller.model';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {CommonModule} from '@angular/common';
import {ShopSellerService} from '../../../core/services/shop-seller/shop-seller.service';
import * as feather from 'feather-icons'
import {FormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {ButtonComponent} from '../../../shared/components/button/button.component';
import {FeatherPipe} from '../../../core/pipes/feather.pipe';
import {getShopSellerRouteId, isMasterDataSeller} from '../../../core/helpers/shop-seller.helper';

@Component({
  selector: 'app-shop-seller-list',
  imports: [
    AlertComponent,
    LoadingComponent,
    PaginationComponent,
    TranslateModule,
    RouterLink,
    CommonModule,
    FormsModule,
    ButtonComponent,
    FeatherPipe
  ],
  templateUrl: './shop-seller-list.component.html',
  styleUrl: './shop-seller-list.component.scss'
})
export class ShopSellerListComponent implements OnInit, OnDestroy {

  error: string | null = null;
  version : 'v1' | 'v2' = 'v1'
  redirectBase = '/shop-sellers/seller';

  shopSellers: ShopSellerListItem[] = [];
  readonly paginationState = createPaginationState();

  // Filter UI model (authorized can be '', 'true', 'false')
  uiFilters: { documentId: string; name: string; email: string; login: string; authorized: string } = {
    documentId: '',
    name: '',
    email: '',
    login: '',
    authorized: ''
  };

  private qpSub?: Subscription;

  readonly getRouteId = getShopSellerRouteId;
  readonly isMasterData = isMasterDataSeller;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private shopSellerService: ShopSellerService
  ) {}

  ngOnInit(): void {
    // React to query param changes (including initial)
    this.qpSub = this.route.queryParams.subscribe(params => {
      const page = parseInt(params['page'] ?? '0', 10);
      const pageSize = parseInt(params['pageSize'] ?? '10', 10);

      // Populate UI filters from URL
      this.uiFilters.documentId = params['documentId'] ?? '';
      this.uiFilters.name = params['name'] ?? '';
      this.uiFilters.email = params['email'] ?? '';
      this.uiFilters.login = params['login'] ?? '';
      this.uiFilters.authorized = params['authorized'] ?? '';
      this.version = this.route.snapshot.data['version'] === 'minimal' ? 'v2' : 'v1';
      this.redirectBase = this.version === 'v2' ? '/shop-sellers-minimal/seller' : '/shop-sellers/seller';

      this.fetchList(page, pageSize);
    });
  }

  ngOnDestroy(): void {
    this.qpSub?.unsubscribe();
  }

  // Convert UI filters to service filter model
  private buildFilter(page: number, pageSize: number): ShopSellerListFilterModel {
    const filter: ShopSellerListFilterModel = {
      page,
      pageSize
    } as ShopSellerListFilterModel;

    if (this.uiFilters.documentId?.trim()) filter.documentId = this.uiFilters.documentId.trim();
    if (this.uiFilters.name?.trim()) filter.name = this.uiFilters.name.trim();
    if (this.uiFilters.email?.trim()) filter.email = this.uiFilters.email.trim();
    if (this.uiFilters.login?.trim()) filter.login = this.uiFilters.login.trim();
    if (this.uiFilters.authorized === 'true') filter.authorized = true;
    if (this.uiFilters.authorized === 'false') filter.authorized = false;

    return filter;
  }

  private fetchList(page: number, pageSize: number): void {
    setPaginationLoading(this.paginationState, true);
    this.error = null;

    const filter = this.buildFilter(page, pageSize);

    this.shopSellerService.getSellerList(filter).subscribe({
      next: (result) => {
        this.shopSellers = result.content;
        updatePaginationFromResult(this.paginationState, result, pageSize);
        feather.replace();
      },
      error: (err) => {
        this.error = 'Falha ao carregar os dados.';
        setPaginationLoading(this.paginationState, false);
        console.error(err);
      }
    });
  }

  // Called by pagination component
  onPageChange = (page: number, pageSize: number): void => {
    // Update URL with current filters + new page params
    this.navigateWithQuery({ page, pageSize });
  }

  onSearch(): void {
    this.navigateWithQuery({ page: 0, pageSize: this.paginationState().itemsPerPage });
  }

  onClear(): void {
    this.uiFilters = { documentId: '', name: '', email: '', login: '', authorized: '' };
    this.navigateWithQuery({ page: 0, pageSize: 10 }, true);
  }

  private navigateWithQuery(pagination: { page: number; pageSize: number }, clearAll?: boolean): void {
    const baseParams: any = {
      page: pagination.page,
      pageSize: pagination.pageSize
    };

    // Use null to remove params from URL when empty/clearing
    const filterParams: any = clearAll ? {
      documentId: null,
      name: null,
      email: null,
      login: null,
      authorized: null
    } : {
      documentId: this.uiFilters.documentId?.trim() ? this.uiFilters.documentId.trim() : null,
      name: this.uiFilters.name?.trim() ? this.uiFilters.name.trim() : null,
      email: this.uiFilters.email?.trim() ? this.uiFilters.email.trim() : null,
      login: this.uiFilters.login?.trim() ? this.uiFilters.login.trim() : null,
      authorized: this.uiFilters.authorized ? this.uiFilters.authorized : null
    };

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { ...filterParams, ...baseParams },
      queryParamsHandling: 'merge'
    });
  }
}
